import { FixedToolbarKit } from 'components/editor/plugins/fixed-toolbar-kit';
import { type Value } from 'platejs';
import { createPlateEditor, usePlateEditor } from 'platejs/react';
import { BasicBlocksKit } from './plugins/basic-blocks-kit';
import { BasicMarksKit } from './plugins/basic-marks-kit';
import { BlockPlaceholderKit } from './plugins/block-placeholder-kit';
import { IndentKit } from './plugins/indent-kit';
import { LinkKit } from './plugins/link-kit';
import { ListKit } from './plugins/list-classic-kit';
import { MediaKit } from './plugins/media-kit';

function toInitialEditorInput(value?: Value | string): Value | string {
  if (typeof value === 'string') {
    if (value.startsWith('<') && value.includes('</')) {
      return value;
    } else {
      return [
        {
          type: 'p',
          children: [{ text: value }],
        },
      ];
    }
  } else if (!value || (Array.isArray(value) && value.length === 0)) {
    return [
      {
        type: 'p',
        children: [{ text: '' }],
      },
    ];
  } else {
    return value;
  }
}

const plugins = [
  ...BasicBlocksKit,
  ...LinkKit,
  ...ListKit,
  ...MediaKit,
  ...BasicMarksKit,
  ...IndentKit,
  ...BlockPlaceholderKit,
  ...FixedToolbarKit,
];

/**
 * Slate only normalizes what an edit touches, so a loaded value that breaks its rules stays broken and
 * makes arrow keys throw. Normalizing in the editor itself would fire onValueChange on mount, so it
 * happens in a throwaway editor instead. As the values are small, this does not create a lot of overhead
 * and is not noticeable in practice.
 */
function toSlateNormalizedValue(input: Value | string): Value {
  return createPlateEditor({ plugins, value: input, shouldNormalizeEditor: true }).children;
}

export const useCreateEditor = ({ value, readOnly }: { value?: Value | string; readOnly?: boolean }) => {
  const initialInput = toInitialEditorInput(value);

  return usePlateEditor({
    plugins,
    value: () => toSlateNormalizedValue(initialInput),
    readOnly,
  });
};
